import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useDistricts } from "@/api/districts/district-queries";
import { useAssignSourceToDistrict } from "@/api/districts/mutations";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";

type Props = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	sn: string;
	currentDistrictId: number | null;
};

export function AssignSourceToDistrictDialog({
	open,
	onOpenChange,
	sn,
	currentDistrictId,
}: Props) {
	const { t } = useTranslation();
	const [districtId, setDistrictId] = useState<string>("");

	useEffect(() => {
		if (open) setDistrictId(currentDistrictId ? String(currentDistrictId) : "");
	}, [open, currentDistrictId]);

	const assign = useAssignSourceToDistrict();
	const { data: districts } = useDistricts();

	function handleClose() {
		onOpenChange(false);
	}

	function handleSubmit() {
		if (!districtId) return;
		assign.mutate(
			{ source_sns: [sn], district_id: Number(districtId) },
			{
				onSuccess: () => {
					toast.success(t("mqttSources.assignToDistrictSuccess"));
					handleClose();
				},
				onError: () => {
					toast.error(t("mqttSources.assignToDistrictError"));
				},
			},
		);
	}

	const canSubmit =
		!!districtId &&
		Number(districtId) !== currentDistrictId &&
		!assign.isPending;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>{t("mqttSources.assignToDistrictTitle")}</DialogTitle>
					<DialogDescription>
						{t("mqttSources.assignToDistrictDescription", { sn })}
					</DialogDescription>
				</DialogHeader>
				<div className="flex flex-col gap-4 py-2">
					<div className="flex flex-col gap-1.5">
						<Label htmlFor="district-select">
							{t("mqttSources.colDistrict")}
						</Label>
						<Select value={districtId} onValueChange={setDistrictId}>
							<SelectTrigger className="w-full">
								<SelectValue placeholder={t("mqttSources.selectDistrict")} />
							</SelectTrigger>
							<SelectContent>
								{(districts ?? []).map((d) => (
									<SelectItem key={d.id} value={String(d.id)}>
										{d.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</div>
				<DialogFooter>
					<Button
						variant="outline"
						onClick={handleClose}
						disabled={assign.isPending}
					>
						{t("mqttSources.cancel")}
					</Button>
					<Button onClick={handleSubmit} disabled={!canSubmit}>
						{t("mqttSources.save")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
